import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { Plus, Trash2, CalendarX, AlertCircle, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import moment from 'moment';
import 'moment/locale/pt-br';
import { getReceptionTeamMemberByUser } from '@/lib/teamMemberLookup';
import {
  fetchGlobalAvailabilityData,
  listGlobalAvailabilityBlocksForMember
} from '@/lib/globalAvailability';
import {
  createSyncedAvailabilityBlocks,
  deleteSyncedAvailabilityBlock,
  invalidateSyncedAvailabilityQueries
} from '@/lib/availabilitySync';

moment.locale('pt-br');

export default function CalendarioDisponibilidadeRecepcao() {
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedDates, setSelectedDates] = useState([]);
  const [blockWholeMonth, setBlockWholeMonth] = useState(false);
  const [reason, setReason] = useState('');
  const [viewAllMode, setViewAllMode] = useState(false);
  const [selectedMemberFilter, setSelectedMemberFilter] = useState('all');

  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: permissions } = useQuery({
    queryKey: ['adminPermissions', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const byUserEmail = await base44.entities.AdminPermission.filter({ user_email: user.email });
      if (byUserEmail.length > 0) return byUserEmail[0];
      const byEmail = await base44.entities.AdminPermission.filter({ email: user.email });
      return byEmail[0] || null;
    },
    enabled: !!user?.email
  });

  const { data: myReceptionMember, isLoading: loadingMyReceptionMember } = useQuery({
    queryKey: ['myReceptionMember', user?.id, user?.email],
    queryFn: () => getReceptionTeamMemberByUser(user),
    enabled: !!user
  });

  const { data: allBlocks = [], isLoading } = useQuery({
    queryKey: ['receptionAvailabilityBlocks'],
    queryFn: () => base44.entities.ReceptionAvailabilityBlock.list('-blocked_date')
  });

  const { data: globalAvailabilityData } = useQuery({
    queryKey: ['globalAvailabilityData'],
    queryFn: fetchGlobalAvailabilityData
  });

  const isAdmin = user?.role === 'admin' || permissions?.is_super_admin;
  const isReceptionLeader = myReceptionMember?.is_leader === true || String(myReceptionMember?.is_leader || '').toLowerCase() === 'true';
  const canManageReception = isAdmin || isReceptionLeader;

  const createMutation = useMutation({
    mutationFn: ({ dates, reason }) => createSyncedAvailabilityBlocks({ user, dates, reason }),
    onSuccess: ({ createdCount }) => {
      void Promise.allSettled([
        invalidateSyncedAvailabilityQueries(queryClient),
        queryClient.invalidateQueries({ queryKey: ['globalAvailabilityData'] })
      ]);
      if (createdCount === 0) {
        toast.info('Todas as datas selecionadas já estão bloqueadas.');
        return;
      }
      toast.success('Bloqueios salvos com sucesso!');
      resetForm();
    },
    onError: (error) => toast.error(error?.message || 'Não foi possível salvar os bloqueios.', { duration: 8000 })
  });

  const deleteMutation = useMutation({
    mutationFn: (block) =>
      deleteSyncedAvailabilityBlock({
        user,
        blockedDate: block?.blocked_date
      }),
    onSuccess: ({ deletedCount }) => {
      void Promise.allSettled([
        invalidateSyncedAvailabilityQueries(queryClient),
        queryClient.invalidateQueries({ queryKey: ['globalAvailabilityData'] })
      ]);
      if (deletedCount === 0) {
        toast.info('Nenhum bloqueio encontrado para remover.');
        return;
      }
      toast.success('Bloqueio removido com sucesso!');
    },
    onError: (error) => toast.error(error?.message || 'Não foi possível remover o bloqueio.')
  });

  const resetForm = () => {
    setSelectedDate('');
    setSelectedDates([]);
    setBlockWholeMonth(false);
    setReason('');
    setShowForm(false);
  };

    const addDateToSelection = (dateStr) => {
    if (!dateStr) return;
    if (moment(dateStr).isBefore(moment(), 'day')) return;
    setSelectedDates((prev) => Array.from(new Set([...prev, dateStr])).sort());
  };

  const removeDateFromSelection = (dateStr) => {
    setSelectedDates((prev) => prev.filter((d) => d !== dateStr));
  };

    const handleSubmit = () => {
    if (!myReceptionMember) {
      toast.error('Você não está cadastrado como integrante da equipe de recepção.');
      return;
    }

    let datesToBlock = [];
    if (blockWholeMonth) {
      const start = currentMonth.clone().startOf('month');
      const end = currentMonth.clone().endOf('month');
      const cursor = start.clone();
      while (cursor.isSameOrBefore(end, 'day')) {
        if (!cursor.isBefore(moment(), 'day')) datesToBlock.push(cursor.format('YYYY-MM-DD'));
        cursor.add(1, 'day');
      }
    } else {
      const fromSelection = selectedDates.length > 0 ? selectedDates : (selectedDate ? [selectedDate] : []);
      datesToBlock = Array.from(new Set(fromSelection)).filter((d) => !moment(d).isBefore(moment(), 'day'));
    }

    if (datesToBlock.length === 0) {
      toast.error('Selecione uma ou mais datas, ou marque mês inteiro.');
      return;
    }

    const myBlockedSet = new Set(myBlocks.map((b) => b.blocked_date));
    const newDates = datesToBlock.filter((d) => !myBlockedSet.has(d));
    if (newDates.length === 0) {
      toast.info('Todas as datas selecionadas já estão bloqueadas.');
      return;
    }

    createMutation.mutate({
      dates: newDates,
      reason
    });
  };
  const myBlocks = allBlocks.filter(block => 
    block.reception_member_id === myReceptionMember?.id
  );
  const myGlobalBlocks = listGlobalAvailabilityBlocksForMember(myReceptionMember, globalAvailabilityData);

  const memberFilterOptions = React.useMemo(() => {
    const optionsById = new Map();
    (allBlocks || []).forEach((block) => {
      const memberId = String(block?.reception_member_id || '');
      if (!memberId) return;
      if (!optionsById.has(memberId)) {
        optionsById.set(memberId, block?.reception_member_name || 'Integrante');
      }
    });
    return Array.from(optionsById.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR'));
  }, [allBlocks]);

  const displayBlocksBase = viewAllMode && canManageReception ? allBlocks : (myGlobalBlocks.length ? myGlobalBlocks : myBlocks);
  const displayBlocks = viewAllMode && canManageReception && selectedMemberFilter !== 'all'
    ? displayBlocksBase.filter((block) => String(block?.reception_member_id || '') === selectedMemberFilter)
    : displayBlocksBase;

  const isDateBlocked = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    return displayBlocks.some(block => block.blocked_date === dateStr);
  };

  const getBlocksForDate = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    return displayBlocks.filter(block => block.blocked_date === dateStr);
  };

  const formatWeekdayPtBr = (dateStr) => {
    const [year, month, day] = String(dateStr || '').split('-').map(Number);
    if (!year || !month || !day) return '';
    return new Date(year, month - 1, day).toLocaleDateString('pt-BR', { weekday: 'long' });
  };

  const renderCalendar = () => {
    const startOfMonth = currentMonth.clone().startOf('month');
    const endOfMonth = currentMonth.clone().endOf('month');
    const startDate = startOfMonth.clone().startOf('week');
    const endDate = endOfMonth.clone().endOf('week');

    const calendar = [];
    const day = startDate.clone();

    while (day.isSameOrBefore(endDate, 'day')) {
      calendar.push(
        Array(7).fill(0).map(() => {
          const currentDay = day.clone();
          day.add(1, 'day');
          return currentDay;
        })
      );
    }

    return (
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(currentMonth.clone().subtract(1, 'month'))}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h3 className="font-semibold text-gray-900">
            {currentMonth.toDate().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
          </h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(currentMonth.clone().add(1, 'month'))}
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {calendar.map((week, weekIndex) => (
            <React.Fragment key={weekIndex}>
              {week.map((day, dayIndex) => {
                const isCurrentMonth = day.month() === currentMonth.month();
                const isToday = day.isSame(moment(), 'day');
                const isBlocked = isDateBlocked(day);
                const isPast = day.isBefore(moment(), 'day');
                const blocks = getBlocksForDate(day);
                const dateStr = day.format('YYYY-MM-DD');
                const isSelected = selectedDates.includes(dateStr);

                return (
                  <div
                    key={dayIndex}
                    className={`
                      aspect-square p-1 text-center text-sm rounded-lg cursor-pointer transition-all
                      ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-900'}
                      ${isToday ? 'bg-blue-50 border border-blue-300' : ''}
                      ${isSelected ? 'ring-2 ring-orange-400 bg-amber-50' : ''}
                      ${isBlocked ? 'bg-red-50 border border-red-300' : 'hover:bg-gray-50'}
                      ${isPast && !isBlocked ? 'opacity-50' : ''}
                    `}
                    onClick={() => {
                      if (isCurrentMonth && !isPast) {
                        setSelectedDate(dateStr);
                        setBlockWholeMonth(false);
                        setSelectedDates((prev) => prev.includes(dateStr) ? prev.filter((d) => d !== dateStr) : Array.from(new Set([...prev, dateStr])).sort());
                        setShowForm(true);
                      }
                    }}
                  >
                    <div className="font-medium">{day.format('D')}</div>
                    {isBlocked && (
                      <div className="flex justify-center mt-0.5">
                        <div className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                      </div>
                    )}
                    {blocks.length > 1 && (
                      <div className="text-[10px] text-red-600 font-medium">
                        {blocks.length}
                      </div>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  if (isLoading || loadingMyReceptionMember || !user) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (!myReceptionMember && !isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <div className="bg-gradient-to-br from-amber-600 to-orange-700 text-white pt-8 pb-6 px-4">
          <div className="max-w-lg mx-auto">
            <PageHeader title="Calendário de Disponibilidade" showBack={true} />
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 py-12">
          <div className="bg-white rounded-xl p-8 shadow-sm text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Acesso Restrito</h3>
            <p className="text-gray-600">Apenas integrantes da equipe de recepção podem acessar esta área.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-amber-600 to-orange-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Calendário de Disponibilidade" 
            subtitle="Bloqueie datas em que não estará disponível"
            showBack={true}
            action={
              canManageReception && (
                <Button 
                  onClick={() => {
                    const nextMode = !viewAllMode;
                    setViewAllMode(nextMode);
                    if (!nextMode) setSelectedMemberFilter('all');
                  }} 
                  size="sm" 
                  className="bg-white text-amber-700 hover:bg-amber-50"
                >
                  {viewAllMode ? 'Meu Calendário' : 'Ver Todos'}
                </Button>
              )
            }
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {renderCalendar()}

        {viewAllMode && canManageReception && (
          <div className="mt-4 bg-white rounded-xl p-4 shadow-sm">
            <Label htmlFor="receptionMemberFilter" className="mb-2 block">Filtrar por integrante</Label>
            <select
              id="receptionMemberFilter"
              value={selectedMemberFilter}
              onChange={(e) => setSelectedMemberFilter(e.target.value)}
              className="w-full h-10 rounded-md border border-gray-200 px-3 text-sm"
            >
              <option value="all">Todos os integrantes</option>
              {memberFilterOptions.map((option) => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="mt-6">
          <h3 className="font-semibold text-gray-900 mb-3">
            {viewAllMode && canManageReception ? 'Todos os Bloqueios' : 'Minhas Datas Bloqueadas'}
          </h3>
          
          {displayBlocks.length === 0 ? (
            <EmptyState
              icon={CalendarX}
              title="Nenhuma data bloqueada"
              description={viewAllMode ? "Nenhum integrante bloqueou datas ainda" : "Você ainda não bloqueou nenhuma data"}
            />
          ) : (
            <div className="space-y-3">
              {displayBlocks.map((block, index) => (
                <motion.div
                  key={block.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="bg-white rounded-xl p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CalendarX className="w-4 h-4 text-red-500" />
                        <span className="font-semibold text-gray-900">
                          {moment(block.blocked_date).format('DD/MM/YYYY')}
                        </span>
                        <Badge variant="secondary" className="bg-red-50 text-red-700">
                          {formatWeekdayPtBr(block.blocked_date)}
                        </Badge>
                      </div>
                      {viewAllMode && canManageReception && (
                        <p className="text-sm text-gray-600 mb-1">{block.reception_member_name}</p>
                      )}
                      {block.reason && (
                        <p className="text-sm text-gray-500">{block.reason}</p>
                      )}
                    </div>
                    {(block.created_by_email === user.email || isAdmin) && !viewAllMode && (
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="text-red-600"
                        onClick={() => {
                          if (confirm('Deseja remover este bloqueio?')) {
                            deleteMutation.mutate(block);
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bloquear Data</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="flex gap-2">
              <Button
                type="button"
                variant={blockWholeMonth ? "default" : "outline"}
                className="flex-1"
                onClick={() => setBlockWholeMonth((prev) => !prev)}
              >
                {blockWholeMonth ? 'Mês inteiro selecionado' : `Bloquear mês inteiro (${currentMonth.format('MM/YYYY')})`}
              </Button>
            </div>

            <div>
              <Label>Adicionar data</Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={moment().format('YYYY-MM-DD')}
                  disabled={blockWholeMonth}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={blockWholeMonth || !selectedDate}
                  onClick={() => addDateToSelection(selectedDate)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>

            {!blockWholeMonth && (
              <div>
                <Label>Datas selecionadas ({selectedDates.length})</Label>
                {selectedDates.length === 0 ? (
                  <p className="text-sm text-gray-500 mt-1">Clique em vários dias no calendário ou adicione pelo campo.</p>
                ) : (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedDates.map((date) => (
                      <Badge key={date} variant="secondary" className="bg-amber-50 text-amber-700 flex items-center gap-1">
                        {moment(date).format('DD/MM')}
                        <button type="button" onClick={() => removeDateFromSelection(date)}>
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <Label>Motivo (opcional)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                lang="pt-BR"
                spellCheck
                autoCorrect="on"
                placeholder="Ex: Viagem, compromisso pessoal, etc."
                rows={3}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button variant="outline" className="w-full sm:flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button 
                className="w-full sm:flex-1 bg-red-600 hover:bg-red-700" 
                onClick={handleSubmit}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Verificando escalas...' : blockWholeMonth ? 'Bloquear mês' : `Bloquear ${Math.max(1, selectedDates.length)} data(s)`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
